// src/components/lists/ImageCountViewsList.jsx
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Image } from "react-bootstrap";
import influencersImg from "../../assets/images/Influencers.jpg"; // default influencer image
import defaultCocktailImg from "../../assets/images/default_cocktail.png"; // default cocktail image
import { Card, CardBody } from "./../../components/card/card.jsx";

const CountBadge = ({ label, count, icon }) => {
  const displayCount = count !== undefined && count !== null ? count : 0;
  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  return (
    <div
      className="d-flex flex-column align-items-center"
      title={`${label}: ${displayCount}`}
      style={{
        fontSize: "1.2rem",
        textAlign: "center",
        margin: "0 1rem 1rem",
      }}
    >
      <i className={icon} style={{ fontSize: "1.0rem", color: themeColor }}></i>
        <span
          className="count-badge-number"
          style={{
            display: "block",
            marginTop: "0.1rem",
            fontWeight: 700,
            fontSize: "0.8rem",
            textAlign: "center",
            width: "60px",
            letterSpacing: 0.5,
            lineHeight: 1.2,
          }}
        >
          {displayCount}
        </span>
    </div>
  );
};


CountBadge.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  icon: PropTypes.string.isRequired,
};

const ImageCountViewsList = ({
  data,
  tabs,
  activeTab,
  onTabChange,
  onLimitChange,
  loading,
  limit,
  height,
  title
}) => {
  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  // Define a fixed height that the list container should maintain.
  const FIXED_HEIGHT = height || "400px";

  // We now always enforce a fixed height regardless of loading state or data.
  const containerStyle = {
    height: FIXED_HEIGHT,
    overflowY: "auto",
    overflowX: "auto",
  };

  const activeStyle = {
    backgroundColor: themeColor,
    border: `1px solid ${themeColor}`,
    color: "#fff",
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };
  const inactiveStyle = {
    backgroundColor: "transparent",
    border: `1px solid ${themeColor}`,
    color: themeColor,
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };

  const fixedRowStyle = {
    height: "100px",
    verticalAlign: "middle",
  };

  const rankContainerStyle = {

    width: "40px",
    textAlign: "center",
    fontSize: "0.9rem",
    fontWeight: "bold",
    color: themeColor,
    borderRight: `1px solid ${themeColor}`,
  };

  const imageContainerStyle = {
    width: "70px",
    height: "70px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  };

  const currentTab = activeTab || (tabs.length > 0 ? tabs[0].id : "");

  // If the data is wrapped in an object, extract the array.
  let extractedData = data;
  if (!Array.isArray(data) && data && data.mixlist) {
    extractedData = data.mixlist;
  }

  return (
    <Card>
      <CardBody>
        {/* Title */}
        {title && (
          <div className="d-flex fw-bold small mb-3">
            <span className="flex-grow-1" style={{ fontSize: 15 }}>
              {title}
            </span>
          </div>
        )}

        {/* Tabs and Limit Input */}
        {/* Tabs + Limit input (theme-aware) */}
        {tabs && tabs.length > 0 && (() => {
          const isThemeBlack = document.body.classList.contains('theme-black');
          const selected = (id) => (activeTab || (tabs[0]?.id ?? '')) === id;

          // BLACK THEME: use .btn.freq-btn inside .theme-black-controls (same as other component)
            if (isThemeBlack) {
              return (
                <div className="d-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 theme-black-controls controls-row">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`btn freq-btn${selected(tab.id) ? ' active is-active' : ''}`}
                      aria-pressed={selected(tab.id)}
                      onClick={() => onTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                  {onLimitChange && (
                    <input
                      type="text"
                      value={limit}
                      onChange={(e) => onLimitChange(e.target.value)}
                      placeholder="Limit"
                      className="form-control limit-input"
                    />
                  )}
                </div>
              );
            }

          // NON-BLACK THEMES: nav-pills with themed border (like your other buttons)
          return (
            <div className="d-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 icv-controls">
              <ul className="nav nav-pills gap-2">
                {tabs.map((tab) => (
                  <li className="nav-item" key={tab.id}>
                    <button
                      type="button"
                      className={`nav-link ${selected(tab.id) ? 'active' : ''}`}
                      onClick={() => onTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              {onLimitChange && (
                <input
                  type="text"
                  value={limit}
                  onChange={(e) => onLimitChange(e.target.value)}
                  placeholder="Limit"
                  className="form-control limit-input"
                />
              )}
            </div>
          );
        })()}


        {/* Scrollable Container with fixed height */}
        <div style={containerStyle}>
          {loading ? (
            <div
              className="h-100 d-flex justify-content-center align-items-center"
              style={{ height: FIXED_HEIGHT }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{
                  color: themeColor,
                  width: "2rem",
                  height: "2rem",
                  borderWidth: "0.2em",
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : Array.isArray(extractedData) && extractedData.length > 0 ? (
            <table className="table table-bordered table-responsive">
              <tbody>
                {extractedData.map((item, index) => (
                  <tr key={item.id || index} style={fixedRowStyle}>
                    <td style={rankContainerStyle}>{index + 1}</td>
                    <td style={{ width: "100px" }}>
                      <div style={imageContainerStyle}>
                        <Image
                          src={
                            currentTab === "influencers"
                              ? influencersImg
                              : currentTab === "mixlists"
                              ? item.playListImage
                                ? encodeURI(item.playListImage)
                                : defaultCocktailImg
                              : (item.brandIcon ||
                                  item.baseSponsorImage ||
                                  item.image ||
                                  defaultCocktailImg)
                          }
                          alt={
                            currentTab === "influencers"
                              ? "Influencer"
                              : currentTab === "mixlists"
                              ? item.playListName || item.name
                              : item.name || item.brandName
                          }
                          rounded
                          fluid
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              currentTab === "influencers"
                                ? influencersImg
                                : defaultCocktailImg;
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            maxWidth: "100%",
                            objectFit: "cover",
                            backgroundColor: "transparent",
                            mixBlendMode: "multiply",
                            backgroundColor: "#fff"
                          }}
                        />
                      </div>
                    </td>
                    <td>
                      {/* Recipes tab: All icons right-aligned in one row */}
                      {currentTab === "recipes" ? (
                        <div
                          className="d-flex flex-row align-items-center"
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                            minWidth: 0,
                          }}
                        >
                          <div style={{
                            textAlign: "left",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden"
                          }}>
                            <h5
                              style={{
                                fontSize: "1.0rem",
                                margin: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 200,
                              }}
                            >
                              {item.name}
                            </h5>
                            {item.brandName && (
                              <p className="text-inverse" style={{ margin: 0, fontSize: "0.85rem" }}>
                                Crafted By: <strong style={{}}>{item.brandName}</strong>
                              </p>
                            )}
                          </div>
                          <div
                            className="d-flex flex-row align-items-center"
                            style={{
                              display: "flex",
                              gap: "1.3rem",
                              justifyContent: "flex-end",
                              flex: 0,
                              marginLeft: "1.3rem",
                            }}
                          >
                            <CountBadge label="Views" count={item.viewCount.toLocaleString()} icon="fa fa-eye" />
                            <CountBadge label="Likes" count={item.likeCount.toLocaleString()} icon="fa fa-thumbs-up" />
                            <CountBadge label="Crafted" count={item.makeCount.toLocaleString()} icon="fa fa-mortar-pestle" />
                            <CountBadge label="Clicks" count={item.viewCount.toLocaleString()} icon="fa fa-mouse-pointer" />
                            <CountBadge label="Shares" count={item.sharedCount.toLocaleString()} icon="fa fa-share-alt" />
                          </div>
                        </div>
                      ) : (
                      // For all other tabs: KEEP EXISTING LAYOUT
                        currentTab === "ingredients" || currentTab === "brands" ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                              marginTop: "1rem",
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%"}}>
                              <h5 style={{ fontSize: "1.1rem", margin: 0 }}>
                                {item.name}
                              </h5>
                              {item.brandName &&
                                (currentTab === "ingredients" ? (
                                  <p
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "grey",
                                      margin: 0,
                                    }}
                                  >
                                    {item.brandName}
                                  </p>
                                ) : (
                                  <div style={{ textAlign: "center" }}>
                                    <h5 style={{ fontSize: "1.1rem", margin: 0 }}>
                                      {item.brandName}
                                    </h5>
                                  </div>
                                ))}
                            </div>
                              <CountBadge
                                label="Repeat"
                                count={item.repeatCount.toLocaleString()}
                                icon="fa fa-repeat"
                              />
                            </div>
                        ) : currentTab === "mixlists" ? (
                          <div
                            className="d-flex flex-row align-items-center"
                            style={{
                              width: "100%",
                              justifyContent: "space-between", // Ensures left and right alignment
                              marginTop: "0.1rem",
                            }}
                          >
                            {/* Left-aligned name and brand */}
                            <div style={{ textAlign: "left", flex: 1 }}>
                              <h5 style={{ fontSize: "1.0rem", margin: 0 }}>
                                {item.playListName || item.name}
                              </h5>
                              {item.brandName && (
                                <p style={{ fontSize: "0.9rem", color: "grey", margin: 0 }}>
                                  {item.brandName}
                                </p>
                              )}
                            </div>

                            {/* Right-aligned counts */}
                            <div
                              style={{
                                display: "flex",
                                gap: "0.8rem",
                                justifyContent: "flex-end",
                                flex: 1,
                              }}
                            >
                              <CountBadge label="Likes" count={item.likeCount.toLocaleString()} icon="fa fa-thumbs-up" />
                              <CountBadge label="Shares" count={item.shareCount.toLocaleString()} icon="fa fa-share-alt" />
                              <CountBadge label="Views" count={item.viewCount.toLocaleString()} icon="fa fa-eye" />
                              <CountBadge label="Recipes" count={item.recipeCount.toLocaleString()} icon="fa fa-list" />
                            </div>
                          </div>
                        ) : currentTab === "influencers" ? (
                          <div style={{ marginTop: "1rem" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                              }}
                            >
                              <div>
                                <h5 style={{ fontSize: "1.1rem", margin: 0 }}>
                                  {item.username}
                                </h5>
                                {item.email && (
                                  <p
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "grey",
                                      margin: 0,
                                    }}
                                  >
                                    {item.email}
                                  </p>
                                )}
                              </div>
                              <CountBadge
                                label="Repeat"
                                count={item.repeatCount.toLocaleString()}
                                icon="fa fa-repeat"
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            className="d-flex flex-row align-items-center"
                            style={{
                              width: "100%",
                              justifyContent: "space-between",
                              marginTop: "0.1rem",
                            }}
                          >
                            {/* Left-aligned name and brand (centered vertically) */}
                            <div style={{ textAlign: "left", flex: 1 }}>
                              <h5 style={{ fontSize: "1.0rem", margin: 0 }}>
                                {item.name}
                              </h5>
                              <p style={{ margin: 0, color: themeColor }}>
                                Crafted By: <strong>{item.brandName || "Unknown"}</strong>
                              </p>
                            </div>

                            {/* Right-aligned counts (centered vertically) */}
                            <div
                              className="d-flex flex-row align-items-center"
                              style={{
                                display: "flex",
                                gap: "0.8rem",
                                justifyContent: "flex-end",
                                flex: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <CountBadge label="Views" count={item.viewCount.toLocaleString()} icon="fa fa-eye" />
                              <CountBadge label="Likes" count={item.likeCount.toLocaleString()} icon="fa fa-thumbs-up" />
                              <CountBadge label="Crafted" count={item.makeCount.toLocaleString()} icon="fa fa-mortar-pestle" />
                              <CountBadge label="Clicks" count={item.viewCount.toLocaleString()} icon="fa fa-mouse-pointer" />
                              <CountBadge label="Shares" count={item.sharedCount.toLocaleString()} icon="fa fa-share-alt" />
                            </div>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>

            </table>
          ) : (
            // Fallback: Even when no records are found, we still use the containerStyle to maintain the fixed height.
            <div 
              className="d-flex justify-content-center align-items-center" 
              style={{ height: "100%" }}
            >
              No records found.
            </div>
          )}
        </div>
      </CardBody>
      {/* Inline styles for responsiveness */}
      <style>{`
        @media (max-width: 768px) {
          .rank-container {
            font-size: 1rem !important;
            text-align: left;
          }
          .image-container {
            width: 50px !important;
            height: 50px !important;
          }
          .table th,
          .table td {
            font-size: 0.85rem !important;
          }
        }
        @media (max-width: 576px) {
          .rank-container {
            font-size: 0.85rem !important;
          }
          .image-container img {
            width: 40px !important;
            height: 40px !important;
          }
          .table th,
          .table td {
            font-size: 0.75rem !important;
          }
        }
      `}</style>
    </Card>
  );
};

ImageCountViewsList.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        image: PropTypes.string,
        baseSponsorImage: PropTypes.string,
        brandName: PropTypes.string,
        brandIcon: PropTypes.string,
        viewCount: PropTypes.number,
        countMake: PropTypes.number,
        countmake: PropTypes.number,
        likeCount: PropTypes.number,
        makeCount: PropTypes.number,
        sharedCount: PropTypes.number,
        repeatCount: PropTypes.number,
        viewListCount: PropTypes.number,
        username: PropTypes.string,
        email: PropTypes.string,
        playListName: PropTypes.string,
        recipeCount: PropTypes.number,
      })
    ),
    PropTypes.object, // in case data is wrapped, e.g., { mixlist: [...] }
  ]).isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  onLimitChange: PropTypes.func,
  loading: PropTypes.bool,
  limit: PropTypes.string,
  height: PropTypes.string,
  title: PropTypes.string,
};

ImageCountViewsList.defaultProps = {
  tabs: [],
  activeTab: "",
  onTabChange: () => {},
  onLimitChange: () => {},
  loading: false,
  limit: "10",
  height: "400px",
  title: "",
};

<style>{`
  .count-badge-number {
    color: #6b7280; /* gray-500, visible on white */
    text-shadow: none;
    transition: color .2s;
  }
  @media (prefers-color-scheme: dark) {
    .count-badge-number {
      color: #fff;
      text-shadow: 0 1px 2px rgba(20,20,40,0.12);
    }
  }
`}</style>

export default ImageCountViewsList;
